import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { toast } from 'sonner';
import {
  BookOpen,
  Calendar,
  Clock,
  Users as UsersIcon,
  CircleDot,
  GraduationCap,
  Loader2,
} from 'lucide-react';

// Fase B-5E.1: nivel derivado del módulo (fuente de verdad: groups.module_slot)
function moduleLevel(m) {
  if (m == null) return null;
  if (m >= 1 && m <= 4) return 'Básico';
  if (m >= 5 && m <= 8) return 'Intermedio';
  if (m >= 9 && m <= 10) return 'Avanzado';
  return null;
}

const DAY_LABEL = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves',
  friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
};
function formatDays(days) {
  if (!Array.isArray(days) || days.length === 0) return null;
  const labels = days.map(d => DAY_LABEL[d] || d);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} y ${labels[1]}`;
  return labels.slice(0, -1).join(', ') + ' y ' + labels[labels.length - 1];
}

export default function TeacherGroups() {
  const [groups, setGroups] = useState([]);

  // Módulo de solicitud de examen de módulo
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersGroup, setMembersGroup] = useState(null);
  const [membersByGroup, setMembersByGroup] = useState({});
  const [membersLoading, setMembersLoading] = useState(false);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  const [examOpen, setExamOpen] = useState(false);
  const [examEnrollment, setExamEnrollment] = useState(null);
  const [teacherComment, setTeacherComment] = useState('');
  const [examSaving, setExamSaving] = useState(false);

  const load = () => api.get('/groups').then(r => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const openMembers = async (group) => {
    setMembersGroup(group);
    setMembersOpen(true);
    setSelectedStudent(null);
    setStudentEnrollments([]);

    if (membersByGroup[group.id]) return;

    setMembersLoading(true);
    try {
      const response = await api.get(`/groups/${group.id}/students`);
      setMembersByGroup(prev => ({
        ...prev,
        [group.id]: response.data || [],
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando alumnos');
    } finally {
      setMembersLoading(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setStudentEnrollments([]);
    setEnrollmentsLoading(true);

    try {
      const response = await api.get(
        `/students/${student.student_id}/module-enrollments`
      );
      setStudentEnrollments(response.data || []);
    } catch (e) {
      setStudentEnrollments([]);
      toast.error(
        e.response?.data?.detail ||
        'No se pudo cargar la trayectoria académica'
      );
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const openExamRequest = (enrollment) => {
    setExamEnrollment(enrollment);
    setTeacherComment('');
    setExamOpen(true);
  };

  const submitExamRequest = async () => {
    if (!selectedStudent || !examEnrollment) return;

    setExamSaving(true);

    try {
      await api.post('/module-exam-requests', {
        student_id: selectedStudent.student_id,
        module_enrollment_id: examEnrollment.id,
        teacher_comment: teacherComment.trim(),
      });

      toast.success(
        `Solicitud de examen del Módulo ${examEnrollment.module_slot} enviada`
      );

      setExamOpen(false);
      setExamEnrollment(null);
      setTeacherComment('');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;

      if (status === 409) {
        toast.error(
          detail || 'Ya existe una solicitud pendiente para este módulo'
        );
      } else if (status === 403) {
        toast.error(
          detail || 'No tienes autorización para solicitar este examen'
        );
      } else {
        toast.error(detail || 'No se pudo enviar la solicitud');
      }
    } finally {
      setExamSaving(false);
    }
  };
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Mis grupos"
        subtitle="Gestiona tus clases"
        testId="groups-header"
      />
      <div className="grid sm:grid-cols-2 gap-3">
        {groups.map(g => {
          const level = moduleLevel(g.module_slot);
          const days = formatDays(g.schedule_days);
          const hasSchedule = g.start_time && g.end_time;
          const isActive = g.active !== false;
          return (
            <div key={g.id}
              data-testid={`teacher-group-card-${g.id}`}
              className="rounded-2xl bg-white border border-slate-200 p-4">
              {g.group_code && (
                <div className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-1"
                  data-testid={`teacher-group-code-${g.id}`}>
                  GRUPO {g.group_code}
                </div>
              )}
              <div className="font-display font-bold text-base" data-testid={`teacher-group-name-${g.id}`}>
                {g.name}
              </div>
              {g.description && <div className="text-sm text-slate-500 mt-0.5">{g.description}</div>}
              <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                {g.module_slot != null && (
                  <div className="flex items-center gap-1.5" data-testid={`teacher-group-module-${g.id}`}>
                    <BookOpen className="w-3.5 h-3.5 text-slate-500"/>
                    Módulo {g.module_slot}{level ? ` · ${level}` : ''}
                  </div>
                )}
                {days && (
                  <div className="flex items-center gap-1.5" data-testid={`teacher-group-days-${g.id}`}>
                    <Calendar className="w-3.5 h-3.5 text-slate-500"/>
                    {days}
                  </div>
                )}
                {hasSchedule && (
                  <div className="flex items-center gap-1.5" data-testid={`teacher-group-hours-${g.id}`}>
                    <Clock className="w-3.5 h-3.5 text-slate-500"/>
                    {g.start_time} – {g.end_time}
                  </div>
                )}
                {typeof g.student_count === 'number' && (
                  <div className="flex items-center gap-1.5" data-testid={`teacher-group-count-${g.id}`}>
                    <UsersIcon className="w-3.5 h-3.5 text-slate-500"/>
                    {g.student_count} alumno{g.student_count === 1 ? '' : 's'}
                  </div>
                )}
                <div className="flex items-center gap-1.5" data-testid={`teacher-group-status-${g.id}`}>
                  <CircleDot className={`w-3.5 h-3.5 ${isActive ? 'text-emerald-500' : 'text-slate-400'}`}/>
                  {isActive ? 'Abierto' : 'Cerrado'}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-9 rounded-xl"
                  data-testid={`teacher-group-members-btn-${g.id}`}
                  onClick={() => openMembers(g)}
                >
                  <UsersIcon className="w-4 h-4 mr-2"/>
                  Ver alumnos
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Alumnos y trayectoria académica */}
      <Dialog
        open={membersOpen}
        onOpenChange={(value) => {
          setMembersOpen(value);
          if (!value) {
            setMembersGroup(null);
            setSelectedStudent(null);
            setStudentEnrollments([]);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {membersGroup
                ? `Alumnos · ${membersGroup.name}`
                : 'Alumnos del grupo'}
            </DialogTitle>
          </DialogHeader>

          {membersLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 mr-2 animate-spin"/>
              Cargando alumnos…
            </div>
          ) : (
            <div className="grid md:grid-cols-[240px_1fr] gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                  Alumnos
                </div>

                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y max-h-[55vh] overflow-y-auto">
                  {(membersByGroup[membersGroup?.id] || []).length === 0 ? (
                    <div className="p-5 text-center text-sm text-slate-500">
                      No hay alumnos en este grupo.
                    </div>
                  ) : (
                    (membersByGroup[membersGroup?.id] || []).map(student => {
                      const isSelected =
                        selectedStudent?.student_id === student.student_id;

                      return (
                        <button
                          key={student.student_id}
                          type="button"
                          data-testid={`teacher-student-${student.student_id}`}
                          onClick={() => selectStudent(student)}
                          className={`w-full text-left px-3 py-3 transition ${
                            isSelected
                              ? 'bg-blue-50 border-l-4 border-blue-600'
                              : 'hover:bg-slate-50 border-l-4 border-transparent'
                          }`}
                        >
                          <div className="font-semibold text-sm truncate">
                            {student.first_name} {student.last_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">
                            {student.username || student.email}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                {!selectedStudent ? (
                  <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center rounded-xl border border-dashed border-slate-300 p-6">
                    <GraduationCap className="w-8 h-8 text-slate-300 mb-2"/>
                    <div className="font-semibold text-slate-700">
                      Selecciona un alumno
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Aquí podrás consultar su trayectoria académica y solicitar un examen de módulo.
                    </div>
                  </div>
                ) : enrollmentsLoading ? (
                  <div className="flex items-center justify-center py-10 text-slate-500">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin"/>
                    Cargando trayectoria…
                  </div>
                ) : (
                  <div>
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-4">
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-400">
                        Alumno
                      </div>
                      <div className="font-display font-bold text-lg text-slate-800 mt-1">
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {selectedStudent.email}
                      </div>
                    </div>

                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Trayectoria académica
                    </div>

                    {studentEnrollments.length === 0 ? (
                      <div className="rounded-xl border border-slate-200 p-6 text-center text-sm text-slate-500">
                        Este alumno no tiene inscripciones académicas.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {studentEnrollments.map(enrollment => {
                          const level = moduleLevel(enrollment.module_slot);
                          const isInProgress =
                            enrollment.status === 'in_progress';

                          return (
                            <div
                              key={enrollment.id}
                              data-testid={`student-enrollment-${enrollment.id}`}
                              className="rounded-xl border border-slate-200 p-4"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-semibold text-slate-800">
                                    Módulo {enrollment.module_slot}
                                    {level ? ` · ${level}` : ''}
                                  </div>

                                  <div className="text-xs text-slate-500 mt-1">
                                    {enrollment.started_at
                                      ? `Inicio: ${new Date(enrollment.started_at).toLocaleDateString('es-MX')}`
                                      : 'Sin fecha de inicio'}
                                  </div>
                                </div>

                                <span
                                  className={`shrink-0 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
                                    isInProgress
                                      ? 'bg-blue-100 text-blue-700'
                                      : enrollment.status === 'passed'
                                        ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {enrollment.status === 'in_progress'
                                    ? 'En progreso'
                                    : enrollment.status === 'passed'
                                      ? 'Aprobado'
                                      : enrollment.status || 'Sin estado'}
                                </span>
                              </div>

                              {isInProgress && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="rounded-full bg-blue-600 hover:bg-blue-700"
                                    data-testid={`request-module-exam-${enrollment.id}`}
                                    onClick={() => openExamRequest(enrollment)}
                                  >
                                    <GraduationCap className="w-4 h-4 mr-1.5"/>
                                    Solicitar examen
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMembersOpen(false)}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación de solicitud de examen */}
      <Dialog
        open={examOpen}
        onOpenChange={(value) => {
          if (!value && !examSaving) {
            setExamOpen(false);
            setExamEnrollment(null);
            setTeacherComment('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar examen de módulo</DialogTitle>
          </DialogHeader>

          {selectedStudent && examEnrollment && (
            <div className="space-y-4">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-400">
                  Alumno
                </div>
                <div className="font-semibold text-slate-800 mt-1">
                  {selectedStudent.first_name} {selectedStudent.last_name}
                </div>

                <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mt-3">
                  Módulo
                </div>
                <div className="font-semibold text-slate-800 mt-1">
                  Módulo {examEnrollment.module_slot}
                  {moduleLevel(examEnrollment.module_slot)
                    ? ` · ${moduleLevel(examEnrollment.module_slot)}`
                    : ''}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">
                  Comentario del profesor
                  <span className="font-normal text-slate-400"> · opcional</span>
                </label>
                <Textarea
                  data-testid="module-exam-teacher-comment"
                  className="mt-2"
                  placeholder="Agrega una observación para la solicitud…"
                  value={teacherComment}
                  onChange={(e) => setTeacherComment(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={examSaving}
              onClick={() => setExamOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={examSaving || !examEnrollment}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
              data-testid="submit-module-exam-request"
              onClick={submitExamRequest}
            >
              {examSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>
                  Enviando…
                </>
              ) : (
                'Enviar solicitud'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
